import {nanoid} from "nanoid"
import Objdict from "objdict"
import GameAnalytics from "@fae/source/analytics.js"

import App from "@fae/source/models/App.js"
import Geometry from "@fae/source/utility/Geometry.js"
import Step from "@fae/source/models/Step.js"
import * as Motions from "@fae/source/engine/animating/utility/Motions.js"
import Timings from "@fae/source/engine/animating/utility/Timings.js"

import * as PlotSteps from "@fae/source/data/plot/steps.js"
import HandleArmor from "@fae/source/data/games/ember/scripts/HandleArmor.js"

import unitJobs from "@fae/source/data/games/ember/unitJobs.jsx"

const COUNT_OF_MONSTERS_BY_TURN = {
    "easy": {
        "1": 0,
        "3": 1,
        "5": 1,
        "7": 1,
        "9": 2,
        "11": 2,
        "13": 2,
        "15": 2,
        "17": 2,
        "19": 2,
        "21": 3,
    },
    "standard": {
        "1": 1,
        "3": 1,
        "5": 2,
        "7": 2,
        "9": 2,
        "11": 2,
        "13": 3,
    },
    "hard": {
        "1": 1,
        "3": 2,
        "5": 2,
        "7": 3,
    },
}

export function SpawnInitialEnemyUnits(chapter) {
    return function(stage) {
        let steps = []

        if(chapter == undefined) return
        // if(chapter.monsterKeys == undefined) {
        //     // if(CHAPTERS[chapter] == undefined) return
        //     chapter = CHAPTERS[chapter]
        // }


        const encounterKeys = Object.keys(stage.encounters)
        encounterKeys.forEach((encounterKey, index) => {
            const spots = stage.random.shuffle(Object.values(stage.world.spots).filter((spot) => {
                return Geometry.isInBounds(stage.encounters[encounterKey], spot.position)
                    && spot.tag == "badspawn"
            }))

            const unitCount = Math.max(4, index + 3)
            for(let i = 0; i < unitCount; i += 1) {
                const spot = spots.pop()
                if(spot == undefined) return

                const monsterKey = stage.random.element(chapter.monsterKeys) || {}
                const monster = unitJobs[monsterKey]
                const unitKey = nanoid()

                steps.push(Step.deltastage({
                    "unit": Objdict.merge({
                        "key": unitKey,
                        "affiliation": "bad",
                        "health": 25,
                        "maxhealth": 30,
                        "title": "???",
                        "subtitle": "Monstrosity",
                        "spriteSize": "4x3",
                        "weaponImageKey": "hidden",
                        "position": {
                            "x": spot.position.x,
                            "y": spot.position.y,
                            "z": spot.position.z,
                        },
                    }, monster)
                }))
            }

            // BOSS
            if(index == encounterKeys.length - 1) {
                const spot = spots.pop()
                const bossmonster = unitJobs[chapter.bossmonsterKey]
                if(spot != undefined
                && bossmonster != undefined) {
                    steps.push(Step.deltastage({
                        "unit": Objdict.merge({
                            "affiliation": "bad",
                            "maxhealth": 50,
                            "subtitle": "Monstrosity",
                            "spriteSize": "4x3",
                            "weaponImageKey": "hidden",
                            "position": {
                                "x": spot.position.x,
                                "y": spot.position.y,
                                "z": spot.position.z,
                            },
                        }, bossmonster)
                    }))
                }
            }
        })

        encounterKeys.forEach((encounterKey, index) => {
            const spots = stage.random.shuffle(Object.values(stage.world.spots).filter((spot) => {
                return Geometry.isInBounds(stage.encounters[encounterKey], spot.position)
                    && spot.tag == "lootspawn"
            }))

            if(spots.length == 0) return

            const unitCount = 3
            for(let i = 0; i < unitCount; i += 1) {
                // const itemKey = stage.random.identifier(5)
                const spot = spots[i]
                if(spot == undefined) return
                steps.push(Step.deltastage({
                    "unit": {
                        ...unitJobs["ember/plant/blackberry"],
                        "position": {
                            "x": spot.position.x,
                            "y": spot.position.y,
                            "z": spot.position.z,
                        },
                        // "inventory": { // inventory or protoinventory?
                        //     "items": {
                        //         [itemKey]: {
                        //             "key": itemKey,
                        //             "name": "Squash",
                        //             "image": require("@fae/source/assets/images/apocalypse-tactics/items/crops/kiwisquash.png"),
                        //         }
                        //     }
                        // }
                    }
                }))
            }
        })

        encounterKeys.forEach((encounterKey, index) => {
            const spots = stage.random.shuffle(Object.values(stage.world.spots).filter((spot) => {
                return Geometry.isInBounds(stage.encounters[encounterKey], spot.position)
                    && spot.tag == "torchspawn"
            }))

            if(spots.length == 0) return

            const unitCount = chapter.torchCount ?? 1
            for(let i = 0; i < unitCount; i += 1) {
                const spot = spots.pop()
                if(spot == undefined) return
                steps.push(Step.deltastage({
                    "unit": {
                        ...unitJobs["ember/torch/1"],
                        "position": {
                            "x": spot.position.x,
                            "y": spot.position.y,
                            "z": spot.position.z,
                        }
                    }
                }))
            }
        })

        return steps
    }
}

export function SpawnIncomingEnemyUnits({spotTag = "badspawnspawn", difficulty = "standard", monsterKeys, isAllColors} = {}) {
    return function(stage) {
        if(stage.flags["MonsterSpawning"] === false) return []

        const steps = []

        if(monsterKeys == undefined) {
            return console.warn("Can not spawn incoming enemy units; no monster keys were provided!!")
        }

        let spots = Object.values(stage.world.spots)
        spots = spots.filter((spot) => spot.tag == spotTag)
        spots = spots.filter((spot) => Geometry.isInBounds(stage.encounters[stage.currentEncounterKey], spot.position))
        spots = spots.filter((spot) => {
            // If there is a unit already at this spot, then don't spawn here.
            return Object.values(stage.units).filter((unit) => {
                return unit.position.x == spot.position.x
                        && unit.position.y == spot.position.y
                        && unit.type != "spot" && unit.type != "zone"
                        && unit.isDead != true && unit.isRemoved != true
            }).length == 0
        })
        if(spots.length == 0) return
        spots = stage.random.shuffle(spots, "shuffle")

        const indexedUnits = Object.keys(stage.units).filter((key) => key.startsWith("newUnit")).map((key) => parseInt(key.split("newUnit")[1])).sort((a,b) => a - b)
        let lastIndex = (indexedUnits[indexedUnits.length - 1]) || 0

        const monsterCount = COUNT_OF_MONSTERS_BY_TURN[difficulty][stage.turn.number] ?? 3
        for(let index = 0; index < monsterCount; index += 1) {
            const spot = spots.pop()
            if(spot == undefined) break
            const unitKey = "newUnit" + (lastIndex + (index + 1))

            const monsterKey = stage.random.shuffle(Object.values(monsterKeys)).pop()
            const protomonster = unitJobs[monsterKey]
            if(protomonster == undefined) {
                console.log("could not find monster", monsterKey)
            }

            const protoelement = (protomonster.items && protomonster.items[0]?.element) ?? protomonster.element // TODO: use equippedItem, once it is defined what is equipped
            let protoIncomingUnit = unitJobs["ember/incoming/" + protoelement] ?? unitJobs["ember/incoming/all"]

            if(isAllColors == true) {
                protoIncomingUnit = unitJobs["ember/incoming/all"]
            }

            steps.push(Step.deltastage({
                "unit": {
                    ...protoIncomingUnit,
                    "key": unitKey,
                    "position": {
                        "x": spot.position.x + (spot.direction?.x * -1 || 0),
                        "y": spot.position.y + (spot.direction?.y * -1 || 0),
                        "z": spot.position.z + (spot.direction?.z * -1 || 0)
                    },
                    "moverange": protomonster.moverange,
                    "attackrange": (protomonster.items && protomonster.items[0]?.attackrange) ?? protomonster.attackrange ?? 1, // TODO: use equippedItem, once it is defined what is equipped
                    "protoMonsterKey": monsterKey,
                }
            }))
            // if(spot.direction != undefined) {
            //     steps.push({
            //         "name": "MoveUnit",
            //         "type": "invoke skill",
            //         "deltastages": [
            //             {
            //                 "mark": 0,
            //                 "unit": {
            //                     "key": unitKey,
            //                     "position": {
            //                         "x": spot.position.x + (spot.direction?.x * -1 || 0),
            //                         "y": spot.position.y + (spot.direction?.y * -1 || 0),
            //                         "z": spot.position.z + (spot.direction?.z * -1 || 0)
            //                     },
            //                 }
            //             },
            //             {
            //                 "mark": 100,
            //                 "timing": Timings.easeOutQuad,
            //                 "motion": Motions.arc(0.5),
            //                 "unit": {
            //                     "key": unitKey,
            //                     "position": {
            //                         "x": spot.position.x,
            //                         "y": spot.position.y,
            //                         "z": spot.position.z,
            //                     }
            //                 }
            //             },
            //             {
            //                 "mark": 0,
            //                 "audio": {
            //                     "triggerKey": "Unit:Move"
            //                 },
            //             },
            //         ]
            //     })
            // }
        }

        return steps
    }
}

export function SpawnEnemyUnits() {
    return function(stage) {
        if(stage.flags["MonsterSpawning"] === false) return []

        const activeEnemies = Object.values(stage.units).filter((unit) => {
            return unit.isDead != true
                && unit.isRemoved != true
                && unit.affiliation == "bad"
                && Geometry.isInBounds(stage.encounters[stage.currentEncounterKey], unit.position)
        })
        if(activeEnemies.length >= 10) {
            return
        }

        let incomingEnemies = stage.random.shuffle(Object.values(stage.units).filter((unit) => {
            return Geometry.isInBounds(stage.encounters[stage.currentEncounterKey], unit.position)
                && unit.spotTag == "incoming"
        }))
        if(incomingEnemies.length == 0) return

        // const flames = Object.values(stage.units).filter((unit) => {
        //     return unit.role == "flame"
        // })
        // if(stage.units.bard != undefined
        // && stage.units.bard.isDead != true) {
        //     flames.push(stage.units.bard)
        // }

        const flames = Object.values(stage.units).filter((unit) => {
            return unit.role == "flame"
                && unit.isDead != true
                && Geometry.isInBounds(stage.encounters[stage.currentEncounterKey], unit.position)
        })
        const beacons = Object.values(stage.units).filter((unit) => {
            return unit.role == "torch"
                && unit.isDead != true
                && Geometry.isInBounds(stage.encounters[stage.currentEncounterKey], unit.position)
        })
        const litBeacons = beacons.filter((beacon) => {
            return flames.find((flame) => {
                return flame.position.x == beacon.position.x
                    && flame.position.y == beacon.position.y
            })
        })
        if(beacons.length > 0
        && litBeacons.length == beacons.length) {
            return
        }

        const steps = []
        incomingEnemies.map((incomingEnemy, index) => {
            // // Spawning from a set of monsters rather than what monster was assigned in the incoming monster.
            // const monsterKey = stage.random.shuffle(Object.values(monsterKeys)).pop()
            // if(unitJobs[monsterKey] == undefined) {
            //     console.log("could not find monster", monsterKey)
            // }
            // const protounit = unitJobs[monsterKey]

            const protounit = unitJobs[incomingEnemy.protoMonsterKey]

            steps.push(Step.deltastage({
                "unit": {
                    ...protounit,
                    "key": stage.random.key(),
                    "affiliation": "bad",
                    "state": "aggroed",
                    "position": {
                        "x": incomingEnemy.position.x,
                        "y": incomingEnemy.position.y,
                        "z": incomingEnemy.position.z,
                    },
                }
            }))
            steps.push(Step.deltastage({
                "units": {[incomingEnemy.key]: undefined}
            }))
        })
        return steps
    }
}

export function MoveEncounter({nextEncounterKey, spawnTag, spotTag, turnNumber} = {}) {
    spotTag = spotTag || "goodspawn"
    return function(stage) {
        const nextEncounter = stage.encounters[nextEncounterKey]

        const alliedUnits = Object.values(stage.units).filter((unit) => {
            return unit.affiliation == stage.player.affiliation
                && unit.isDead != true
                // && Geometry.isInBounds(currentEncounter, unit.position) // SHOULD WE RE-ENABLE HTIS??
                && unit.properties?.includes("grunt") != true
        })

        let spawnSpots = Object.values(stage.units).filter((unit) => {
            return (unit.type == "zone" || unit.type == "spot")
                && (unit.tag == spawnTag || spawnTag == undefined)
                && Geometry.isInBounds(nextEncounter, unit.position)
        })

        let spots = stage.world.spots
        if(Object.values(spots)[0] instanceof Array) {
            spots = spots[spotTag]
        } else {
            spots = Object.values(spots).filter((spot) => spot.tag == spotTag)
        }
        if(spots.length > 0) {
            spots = spots.filter((spot) => {
                const collidingUnits = Object.values(stage.units).filter((unit) => {
                    return unit.position.x == spot.position.x
                        && unit.position.y == spot.position.y
                        && unit.position.z == spot.position.z
                        && unit.type != "spot" && unit.type != "zone"
                })

                return collidingUnits.length == 0
                    && (Geometry.isInBounds(nextEncounter, spot.position))
            })

            if(spots.length > 0) spawnSpots = spots
        }

        spawnSpots = stage.random.shuffle(spawnSpots)

        let activeEncounterKeys = undefined
        if(stage.activeEncounterKeys) {
            activeEncounterKeys = [...stage.activeEncounterKeys]
            const currentEncounterIndex = activeEncounterKeys.indexOf(stage.currentEncounterKey)
            activeEncounterKeys[currentEncounterIndex] = nextEncounterKey
            nextEncounterKey = stage.activeEncounterKeys[(currentEncounterIndex + 1) % stage.activeEncounterKeys.length]
        }

        const enemyUnits = Object.values(stage.units).filter((unit) => {
            return unit.isDead != true
                && unit.affiliation == "bad"
                && Geometry.isInBounds(stage.encounters[nextEncounterKey], unit.position)
        })

        return [
            HandleArmor(),
            {
                "name": "SavestateCapture",
                "savestateSlug": "advanceToEncounter" + nextEncounterKey,
            },
            {
                "name": "HideEnemyUnits",
                "deltastages": [
                    enemyUnits.map((unit, index) => {
                        if(unit.properties?.includes("no entrance animation")) return
                        return {
                            "unit": {
                                "key": unit.key,
                                "isRemoved": true,
                            }
                        }
                    })
                ]
            },
            {
                "name": "JumpToDifferentChapterEncounter",
                "duration": 500,
                "deltastages": [
                    {"currentEncounterKey": nextEncounterKey},
                    {"activeEncounterKeys": activeEncounterKeys},
                    {"turn": {"number": turnNumber || 0}}
                ]
            },
            {
                "name": "MoveUnits",
                "type": "invoke skill", // TODO: why is this still required
                "deltastages": [
                    alliedUnits.map((unit, index) => {
                        const spawnSpot = spawnSpots[index]
                        if(spawnSpot == undefined) return undefined
                        return {
                            "mark": 0 + (index * 50),
                            "unit": {
                                "key": unit.key,
                                "position": {
                                    "x": spawnSpot.position.x,
                                    "y": spawnSpot.position.y + (spawnSpot.position.y < 9 / 2 ? -1 : +1),
                                    "z": spawnSpot.position.z,
                                },
                                "isRemoved": false,
                                "energy": {
                                    "invoke skill": true,
                                    "move": true,
                                }
                            }
                        }
                    }),
                    alliedUnits.map((unit, index) => {
                        const spawnSpot = spawnSpots[index]
                        if(spawnSpot == undefined) return undefined
                        return {
                            "mark": 150 + (index * 50),
                            "timing": Timings.easeOutQuad,
                            "motion": Motions.arc(0.5),
                            "unit": {
                                "key": unit.key,
                                "position": {
                                    "x": spawnSpot.position.x,
                                    "y": spawnSpot.position.y,
                                    "z": spawnSpot.position.z,
                                }
                            }
                        }
                    }),
                ]
            },
            {"func": () => GameAnalytics.reportStageSuccess("chapter-" + stage.chapterSlug, "encounter-" + stage.currentEncounterKey)},
            {"func": () => GameAnalytics.reportStageStart("chapter-" + stage.chapterSlug, "encounter-" + nextEncounterKey)},
            {"func": () => App.game.chapter.selection.pointer = {"position": {"x": 16 / 2, "y": 9 / 2}, "mode": "none"}},
            PlotSteps.Wait(500),

            // MAKE THEM INVISIBLE BEFORE THE MOVE ENCOUNTER!!!

            enemyUnits.map((enemy) => {
                if(enemy.properties?.includes("no entrance animation")) return
                return {
                    "name": "MoveUnit",
                    "type": "invoke skill",
                    "deltastages": [
                        {
                            "mark": 0,
                            "unit": {
                                "key": enemy.key,
                                "isRemoved": false,
                                "position": {
                                    "x": enemy.position.x + (enemy.position.x < nextEncounter.center.x ? -1 : +1),
                                    "y": enemy.position.y,
                                    "z": enemy.position.z
                                },
                            }
                        },
                        {
                            "mark": 150,
                            "timing": Timings.easeOutQuad,
                            "motion": Motions.arc(0.5),
                            "unit": {
                                "key": enemy.key,
                                "position": {
                                    "x": enemy.position.x,
                                    "y": enemy.position.y,
                                    "z": enemy.position.z,
                                }
                            }
                        },
                        {
                            "mark": 0,
                            "audio": {
                                "triggerKey": "Unit:Move"
                            },
                        },
                    ]
                }
            }),
        ]
    }
}

import CookingModal from "./modals/CookingModal.jsx"

export function Ending() {
    return [
        // TODO: add a new victory screen that includs crops foraged.
        PlotSteps.ChapterSuccess(),

        PlotSteps.TransitionStart({"isUnskippable": true}),
        PlotSteps.Wait(250, {"isUnskippable": true}),
        // TODO: only render this poem if somebody had died. Add a portrait of the character, grayscaled...?
        // PlotSteps.BlackoutDialogue(`
        //     We too are such stuff as dreams are made of
        //     If we only only dare to dream
        // `),
        PlotSteps.UpdatePlayerParty({"permadamage": true, "permadeath": true}),
        PlotSteps.WaitingModal({"isUnskippable": true, "duration": 2000}),
        // TODO: navigate and setup the campfire encounter??
        PlotSteps.TransitionEnd({"isUnskippable": true}),

        // TODO: script a modal where you can do all these things!!
        CookingModal(), // "4a. Review your food stores, cook for the camp",
        "4b. Forge/repair weapons.",
        "4c. Kindle the flame",
        "4d. Support conversations!!! And other events in the queue, like joinups!!",
        "5. Embark!!",

        // TODO: Should the following events technically be part of the NEXT chapter's start script? Yes, absolutely.
        PlotSteps.TransitionStart({"isUnskippable": true}),
        PlotSteps.WaitingModal({"isUnskippable": true, "duration": 2000, "label": "Saving"}),
        PlotSteps.PlayAudio("Ember:Music:Wander"),
        PlotSteps.Go("/games/ember/overworlds/1"), // TODO: navigate to the correct overworld - you'll also want to pan dramatically over it here.
        PlotSteps.ChapterStart(["7. Flash the title", "of the next chapter."], {"isUnskippable": true}),
        "8. Vamp on the overworld map, giving prose about the next area. Show what monsters are upcoming.",
        "9. Show progress of the fog and such.",
        "10. Ask who they want to bring into this fight. Maybe re-equip some items.",
    ]
}
